import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Smartphone, Share, PlusSquare, MoreVertical, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const KEY = "install-dismissed-at";
const standalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isMobile = () => /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent) || isIOS();
const snoozed = () => Date.now() - Number(localStorage.getItem(KEY) || 0) < 7 * 864e5;

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3"><span className="w-6 h-6 shrink-0 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-slate-700 flex items-center gap-1.5 flex-wrap">{children}{Icon && <Icon size={16} className="text-sky-700" />}</span></li>
);

function HowTo({ open, onOpenChange }) {
  const ios = isIOS();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="install-howto-dialog">
        <DialogHeader>
          <DialogTitle>Install WindWorkHub</DialogTitle>
          <DialogDescription>It takes 10 seconds and works like a normal app.</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 mt-2">
          {ios ? <>
            <Step n={1} icon={Share}>Open this page in Safari and tap the Share button</Step>
            <Step n={2} icon={PlusSquare}>Choose "Add to Home Screen"</Step>
            <Step n={3}>Tap "Add". Open WindWorkHub from your home screen</Step>
          </> : <>
            <Step n={1} icon={MoreVertical}>Tap the browser menu</Step>
            <Step n={2}>Choose "Install app" or "Add to Home screen"</Step>
            <Step n={3}>Confirm. WindWorkHub appears on your home screen</Step>
          </>}
        </ol>
      </DialogContent>
    </Dialog>
  );
}

export default function InstallBanner() {
  const { pathname } = useLocation();
  const [prompt, setPrompt] = useState(() => window.__installPrompt || null);
  const [show, setShow] = useState(() => isMobile() && !standalone() && !snoozed());
  const [howTo, setHowTo] = useState(false);
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); window.__installPrompt = e; setPrompt(e); };
    const onInstalled = () => setShow(false);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);
  if (!show || pathname.startsWith("/messages")) return null;
  const dismiss = () => { localStorage.setItem(KEY, String(Date.now())); setShow(false); };
  const install = async () => {
    if (!prompt) return setHowTo(true);
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    window.__installPrompt = null; setPrompt(null);
    if (outcome === "accepted") setShow(false);
  };
  return (
    <>
      <div className="md:hidden h-24" aria-hidden />
      <div className="md:hidden fixed inset-x-3 bottom-[4.5rem] z-40 rise panel p-3 shadow-xl bg-slate-900 text-white border-slate-900 flex items-center gap-3" data-testid="install-banner">
        <div className="w-10 h-10 shrink-0 rounded-lg bg-amber-400 text-slate-950 flex items-center justify-center"><Smartphone size={20} /></div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm">Get the WindWorkHub app</p>
          <p className="text-xs text-slate-300">Jobs and messages one tap away on your phone</p>
        </div>
        <Button size="sm" onClick={install} className="bg-amber-400 text-slate-950 hover:bg-amber-300" data-testid="install-banner-button">Install</Button>
        <button aria-label="Dismiss" onClick={dismiss} data-testid="install-banner-dismiss" className="text-slate-400"><X size={16} /></button>
      </div>
      <HowTo open={howTo} onOpenChange={setHowTo} />
    </>
  );
}
